import { useCallback, useContext, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import usePrevious from '../../hooks/usePrevious';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';
import { setCurrentPage } from '@src/actions/stencils/editor';

function MonitorUnload(): null {
  const dispatch = useDispatch();
  const ignoreNextTransitionRef = useRef(false);
  const { activePage: currentStep, pageIsDirty } = useContext(
    WysiwygEditorStateContext
  );
  const prevCurrentStep = usePrevious(currentStep);
  const handleLeavePage = useCallback(
    (e) => {
      if (typeof window === 'undefined') return;

      if (!window.allowUnload && pageIsDirty) {
        // Most browsers ignore this string and display their own
        const confirmationMessage = 'Your changes have not been saved yet';
        e.returnValue = confirmationMessage; // Gecko, Trident, Chrome 34+

        return confirmationMessage; // Gecko, WebKit, Chrome <34
      }
    },
    [pageIsDirty]
  );
  const handleUnwindPageNavigation = useCallback(() => {
    if (typeof window === 'undefined') return;

    // if we are already at the first step, do nothing
    if (currentStep === 'build') return;

    window.unwindingPageNavigation = true;

    // go back one step in history, which will put us at the new page
    window.history.back();
  }, [currentStep]);

  const handlePopState = useCallback(
    (e) => {
      if (typeof window === 'undefined') return;

      // Before navigating to the next url, we want to unwind the page
      // navigation using the History API. Otherwise, if the user hits the
      // back button, they will have an extra page in their history and it
      // causes weirdness when they click the back and forward buttons.
      if (window.unwindingPageNavigation) return;

      ignoreNextTransitionRef.current = true;

      if (e.state && e.state.currentStep && e.state.currentStep === 'preview') {
        dispatch(setCurrentPage('preview'));
      } else {
        dispatch(setCurrentPage('build'));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.addEventListener('beforeunload', handleLeavePage);
    window.addEventListener('popstate', handlePopState);
    window.unwindPageNavigation = handleUnwindPageNavigation;

    return () => {
      window.removeEventListener('beforeunload', handleLeavePage);
      window.removeEventListener('popstate', handlePopState);
      window.unwindPageNavigation = () => {};
    };
  }, [handleLeavePage, handlePopState, handleUnwindPageNavigation]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (currentStep === 'preview' && prevCurrentStep === 'build') {
      if (ignoreNextTransitionRef.current) {
        ignoreNextTransitionRef.current = false;
      } else {
        window.history.pushState({ currentStep: 'preview' }, document.title);
      }
    }

    if (currentStep === 'build' && prevCurrentStep === 'preview') {
      if (ignoreNextTransitionRef.current) {
        ignoreNextTransitionRef.current = false;
      } else {
        window.history.back();
      }
    }
  }, [currentStep, prevCurrentStep]);

  return null;
}

export default MonitorUnload;
